import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { api, fmtUsd, fmtTimeAgo, INTENT_COLORS, STATUS_COLORS } from "@/lib/nexus";
import { X, Sparkles, Loader2, ArrowRight, Cpu, Wrench, ShieldCheck, Radio, MapPin, Building2, Globe, Server, Fingerprint, Crosshair } from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "composition", label: "Composition" },
  { key: "intent", label: "Intent" },
  { key: "physical", label: "Physical" },
  { key: "history", label: "History" },
  { key: "network", label: "A2A" },
];

function Row({ label, value, mono = true }) {
  return (
    <div className="flex justify-between items-baseline gap-4 py-1.5 border-b border-nexus-border/60">
      <span className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-3">{label}</span>
      <span className={`text-[12px] ${mono ? "font-mono" : "font-outfit"} text-nexus-text text-right`}>{value}</span>
    </div>
  );
}

function Chip({ text, color = "#00F0FF" }) {
  return (
    <span className="text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5"
      style={{ borderColor: color, color }}>{text}</span>
  );
}

export default function AgentInspector({ agentId, onClose, onTrace }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!agentId) return;
    setData(null); setAnalysis(null); setTab("overview");
    api.get(`/agents/${agentId}`).then((r) => setData(r.data));
  }, [agentId]);

  const runAnalysis = async () => {
    setLoading(true); setAnalysis(null);
    try {
      const r = await api.post("/analyze/agent", { agent_id: agentId });
      setAnalysis(r.data.analysis);
    } catch { setAnalysis("Analysis unavailable."); } finally { setLoading(false); }
  };

  if (!agentId) return null;

  return (
    <AnimatePresence>
      <motion.aside
        key={agentId}
        initial={{ x: 460, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 460, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        className="absolute top-0 right-0 h-full w-[430px] bg-nexus-panel/95 backdrop-blur-lg border-l border-nexus-border z-20 flex flex-col shadow-[-8px_0_40px_rgba(0,0,0,0.6)]"
        data-testid="agent-inspector"
      >
        {!data ? (
          <div className="flex items-center justify-center h-full text-nexus-text-2">
            <Loader2 className="animate-spin" size={18} />
          </div>
        ) : (() => {
          const a = data.agent;
          const color = STATUS_COLORS[a.status];
          return (
            <>
              <div className="p-4 border-b border-nexus-border">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color, boxShadow: `0 0 10px ${color}` }} />
                      <h2 className="font-rajdhani font-bold text-xl tracking-widest uppercase truncate">{a.name}</h2>
                    </div>
                    <div className="hash mt-1">{a.short_address}</div>
                    <div className="mt-2 flex gap-1.5 flex-wrap">
                      <Chip text={a.framework} color="#9D4EDD" />
                      <Chip text={a.chain} color="#00F0FF" />
                      <Chip text={a.status.toUpperCase()} color={color} />
                    </div>
                  </div>
                  <button
                    data-testid="inspector-close"
                    onClick={onClose}
                    className="text-nexus-text-3 hover:text-nexus-text"
                    aria-label="Close inspector"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div>
                    <div className="panel-title text-[9px]">Balance</div>
                    <div className="metric-hero text-base">{fmtUsd(a.balance_usd)}</div>
                  </div>
                  <div>
                    <div className="panel-title text-[9px]">Risk</div>
                    <div className="metric-hero text-base" style={{ color }}>{a.risk_score}</div>
                  </div>
                  <div>
                    <div className="panel-title text-[9px]">Tx</div>
                    <div className="metric-hero text-base">{a.tx_count.toLocaleString()}</div>
                  </div>
                </div>
              </div>

              <div className="flex border-b border-nexus-border">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`inspector-tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`flex-1 py-2 text-[10px] font-mono uppercase tracking-widest border-b-2 transition ${
                      tab === t.key
                        ? "border-nexus-cyan text-nexus-cyan"
                        : "border-transparent text-nexus-text-2 hover:text-nexus-text"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-auto p-4 space-y-3">
                {tab === "overview" && (
                  <>
                    <Row label="Owner" value={a.owner} mono={false} />
                    <Row label="Framework" value={a.framework} />
                    <Row label="Model" value={a.composition.model} />
                    <Row label="Chain" value={a.chain} />
                    <Row label="Address" value={<span className="text-nexus-cyan">{a.short_address}</span>} />
                    <Row label="Region" value={<span className="inline-flex items-center gap-1"><MapPin size={11} className="text-nexus-cyan" /> {a.region_name}</span>} />
                    <Row label="Reputation" value={a.reputation} />
                    <Row label="First seen" value={fmtTimeAgo(a.first_seen) + " ago"} />
                    <Row label="Last active" value={fmtTimeAgo(a.last_active) + " ago"} />

                    <div className="border-t border-nexus-border pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <div className="panel-title"><span className="dot" style={{ background: "#9D4EDD", boxShadow: "0 0 8px #9D4EDD" }} />AI Risk Analysis</div>
                        <button
                          data-testid="inspector-analyze-btn"
                          onClick={runAnalysis}
                          disabled={loading}
                          className="text-[10px] font-mono uppercase tracking-widest text-nexus-violet border border-nexus-violet/50 hover:bg-nexus-violet/10 px-2 py-0.5 flex items-center gap-1 disabled:opacity-50"
                        >
                          {loading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Run
                        </button>
                      </div>
                      {analysis && (
                        <div data-testid="inspector-analysis-output" className="mt-2 text-[12px] leading-relaxed text-nexus-text whitespace-pre-line">
                          {analysis}
                        </div>
                      )}
                      {loading && <div className="mt-2 text-[11px] font-mono uppercase tracking-widest text-nexus-violet">Correlating…</div>}
                    </div>
                  </>
                )}

                {tab === "composition" && (
                  <div className="space-y-4">
                    <div>
                      <div className="panel-title mb-2"><Cpu size={12} className="inline mr-1" /> Model</div>
                      <div className="border border-nexus-border p-2 font-mono text-[12px] text-nexus-cyan">{a.composition.model}</div>
                    </div>
                    <div>
                      <div className="panel-title mb-2"><Wrench size={12} className="inline mr-1" /> Tools</div>
                      <div className="flex flex-wrap gap-1.5">
                        {a.composition.tools.map((t) => <Chip key={t} text={t} color="#00F0FF" />)}
                      </div>
                    </div>
                    <div>
                      <div className="panel-title mb-2"><ShieldCheck size={12} className="inline mr-1" /> Guardrails</div>
                      <div className="flex flex-wrap gap-1.5">
                        {a.composition.guardrails.map((g) => <Chip key={g} text={g} color="#FFB000" />)}
                      </div>
                    </div>
                    <div>
                      <div className="panel-title mb-2"><Building2 size={12} className="inline mr-1" /> Controllers</div>
                      {a.composition.controllers.map((c) => (
                        <div key={c} className="hash py-0.5 truncate">{c}</div>
                      ))}
                    </div>
                    <div>
                      <div className="panel-title mb-2"><Radio size={12} className="inline mr-1" /> Endpoints</div>
                      {a.composition.endpoints.map((e) => (
                        <div key={e} className="text-[11px] font-mono text-nexus-text-2 py-0.5 break-all">{e}</div>
                      ))}
                    </div>
                  </div>
                )}

                {tab === "intent" && (
                  <div className="space-y-3">
                    <div>
                      <div className="panel-title mb-1">Declared Purpose</div>
                      <div className="text-[13px] text-nexus-text">{a.intent_schema.declared_purpose}</div>
                    </div>
                    <div>
                      <div className="panel-title mb-2">Capabilities</div>
                      <div className="flex flex-wrap gap-1.5">
                        {a.intent_schema.capabilities.map((c) => <Chip key={c} text={c} color="#9D4EDD" />)}
                      </div>
                    </div>
                    <div>
                      <div className="panel-title mb-2">Expected On-Chain Patterns</div>
                      <ul className="space-y-1">
                        {a.intent_schema.expected_patterns.map((p) => (
                          <li key={p} className="text-[12px] text-nexus-text-2 font-mono">— {p}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="panel-title mb-2">Declared Intent Tags</div>
                      <div className="flex flex-wrap gap-1.5">
                        {a.intent_tags.map((t) => <Chip key={t} text={t} color="#9D4EDD" />)}
                      </div>
                    </div>
                  </div>
                )}

                {tab === "physical" && a.physical && (
                  <div className="space-y-3">
                    <div className="border border-nexus-border p-3">
                      <div className="panel-title mb-2"><MapPin size={12} className="inline mr-1" /> Physical Location</div>
                      <div className="text-[13px] text-nexus-text font-outfit">{a.physical.street_address}</div>
                      <div className="text-[12px] text-nexus-text-2 mt-0.5">{a.physical.city} · {a.physical.country}</div>
                      <div className="hash mt-1">{a.physical.lat.toFixed(4)}, {a.physical.lng.toFixed(4)}</div>
                      <Link
                        to={`/street/${a.id}`}
                        data-testid="open-street-view"
                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/40 hover:bg-nexus-cyan/10 px-2 py-1"
                      >
                        <Crosshair size={11} /> Open Street View
                      </Link>
                    </div>

                    <div>
                      <div className="panel-title mb-2"><Globe size={12} className="inline mr-1" /> Network</div>
                      <Row label="IP" value={<span className="text-nexus-cyan">{a.physical.ip}</span>} />
                      <Row label="ASN" value={a.physical.asn} />
                      <Row label="ISP" value={<span className="font-outfit">{a.physical.isp}</span>} mono={false} />
                    </div>

                    <div>
                      <div className="panel-title mb-2"><Server size={12} className="inline mr-1" /> Device Fingerprint</div>
                      <Row label="Hosting" value={a.physical.hosting_provider} />
                      <Row label="OS" value={a.physical.device_os} />
                      <Row label="Runtime" value={a.physical.runtime} />
                    </div>

                    <div className="border border-nexus-border p-3">
                      <div className="flex justify-between items-baseline">
                        <div className="panel-title"><Fingerprint size={12} className="inline mr-1" /> Physical Risk Score</div>
                        <div
                          className="font-rajdhani font-bold text-2xl"
                          style={{
                            color: a.physical.physical_risk_score >= 60 ? "#FF3366" :
                                   a.physical.physical_risk_score >= 30 ? "#FFB000" : "#00F0FF",
                          }}
                        >{a.physical.physical_risk_score}</div>
                      </div>
                      <div className="mt-2 space-y-1">
                        {a.physical.physical_risk_factors.length === 0 ? (
                          <div className="text-[12px] text-nexus-text-2">No elevated physical-layer risk factors.</div>
                        ) : a.physical.physical_risk_factors.map((f) => (
                          <div key={f} className="text-[11px] font-mono text-nexus-text-2">— {f}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {tab === "history" && (
                  <div className="relative">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-nexus-border" />
                    {a.history.map((h) => (
                      <div key={`${h.ts}-${h.type}-${h.label || h.detail || ""}`} className="relative pl-6 pb-4">
                        <span
                          className="absolute left-[3px] top-1.5 w-2 h-2 rounded-full"
                          style={{
                            background: h.type === "flag" ? "#FF3366" : "#00F0FF",
                            boxShadow: `0 0 6px ${h.type === "flag" ? "#FF3366" : "#00F0FF"}`,
                          }}
                        />
                        <div className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-3">
                          {new Date(h.ts).toISOString().slice(0, 19).replace("T", " ")} UTC
                        </div>
                        <div className={`text-[12px] mt-0.5 ${h.type === "flag" ? "text-nexus-rose" : "text-nexus-text"}`}>{h.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {tab === "network" && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="panel-title">Recent A2A Transactions</div>
                      <button
                        data-testid="inspector-trace-btn"
                        onClick={() => onTrace?.(a.id)}
                        className="text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/40 hover:bg-nexus-cyan/10 px-2 py-0.5"
                      >
                        TRACE ON GRAPH
                      </button>
                    </div>
                    <div className="space-y-1">
                      {data.recent_transactions.slice(0, 12).map((t) => {
                        const outgoing = t.from_agent_id === a.id;
                        return (
                          <div key={t.id} className="border border-nexus-border/60 px-2 py-1.5 text-[11px] font-mono flex items-center justify-between gap-2 hover:bg-nexus-panel-2/40" data-testid={`inspector-tx-${t.id}`}>
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className={outgoing ? "text-nexus-rose" : "text-nexus-cyan"}>{outgoing ? "OUT →" : "← IN"}</span>
                              <span className="text-nexus-text truncate">{outgoing ? t.to_name : t.from_name}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-[9px] uppercase px-1 border" style={{ borderColor: INTENT_COLORS[t.intent], color: INTENT_COLORS[t.intent] }}>
                                {t.intent}
                              </span>
                              <span className="text-nexus-text">{fmtUsd(t.value_usd)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </motion.aside>
    </AnimatePresence>
  );
}
